import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { TextBlock } from '@/components/text/TextBlock';
import { useDispatch } from 'react-redux';
import { formatNumber } from '@/utils/formatValues';
import {
  PointIcon,
  DownIcon,
  ActiveIcon,
  WrapperContainer,
  FooterDescription,
  SelectorContainer,
  Row,
  ProgramName,
  DescriptionContainer,
  Verbiage,
  Col,
  Footer,
  ContainerPrograms,
  CheckedIcon,
  VerbiageContainer,
  ContainerIcon,
  DetailsContainer,
  Program,
  DivisorIcon,
  InfoIcon,
  ContainerInfoIcon,
  TitleContainer,
  InitialProgramName,
  FooterNote,
} from './AvailablePrograms.styles';
import { TypeOfText } from '@/components/text/TextBlock';
import { useAppSelector } from '@app/hooks';
import { DefaultModal } from '../Modals/DefaultModal/DefaultModal';
import { HandleModal } from '@/utils/handleInfoIcon';
import _ from 'lodash';
import { useIsMobile } from '@/hooks/useIsMobile';
import { handleProgramSelection } from '@/utils/handleVmo';
import { typeOfAmount } from '@/utils/typeOfAmount';
import { pages } from '@/defaultVerbiages';
interface AvailableProgramsProps {
  type?: string;
  name?: string;
  lineAmount?: string;
  estimatedApr?: string;
  minLineAmount?: string;
  maxLineAmount?: string;
  verbiage?: string;
  backgroundColor?: string;
  textColorProgramName?: string;
  textColorVerbiage?: string;
  showLineAmount?: boolean;
  showEstimatedApr?: boolean;
  showEstimatedMonthlyPmt?: boolean;
  isBackgroundCustomColor?: boolean;
  isVerbiageCustomColor?: boolean;
  isTextCustomColor?: boolean;
  isVerbiageCustomBackground?: boolean;
  backgroundColorVerbiage?: string;
  titleIsCustomColor?: boolean;
  titleCustomColor?: string;
  isHidden?: boolean;
}
interface Program {
  apr: number;
  programType: string;
  programName: string;
  maxAmountApproved: number;
  description: string;
  term: string;
  lineAmount: string;
  estimatedMonthlyPayment: string;
  estimatedApr: string;
  minLineAmount: string;
  maxLineAmount: string;
  customVerbiage: string;
  originationFee: string;
  minDrawAmount: string;
  drawAmount?: number;
  alias?: string;
}
const AvailablePrograms: React.FC<AvailableProgramsProps> = ({
  backgroundColor = '#5140E9',
  textColorProgramName = '#ffffff',
  textColorVerbiage = undefined,
  isVerbiageCustomColor = false,
  isBackgroundCustomColor = false,
  isTextCustomColor = false,
  isVerbiageCustomBackground = false,
  backgroundColorVerbiage = '#5140E9',
  titleIsCustomColor = false,
  titleCustomColor = '#5140E9',
  isHidden = false,
}) => {
  const dispatch = useDispatch();
  const loanApplication = useAppSelector((state) => state.LoanApplication);
  const [openModal, setOpenModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<Program>();
  const [openPrograms, setOpenPrograms] = useState<Set<string>>(new Set());
  const [infoIcon, setInfoIcon] = useState({});
  let templateConfig: any = useAppSelector((state) => state.TemplateConfig);
  let LoanApplication = useAppSelector((state) => state.LoanApplication);
  const [programName, setProgramName] = useState('');
  const isICE = templateConfig?.isICE ?? false;
  const frontProgramType = useMemo(() => {
    let frontProgramType;
    if (isICE) {
      frontProgramType = loanApplication?.selectedProgramType === 'Line of Credit' && loanApplication?.availablePrograms.helocRates !== undefined ? 'helocRates' : 'helRates';
    } else {
      frontProgramType = loanApplication?.selectedProgramType === 'Line of Credit' && loanApplication?.availablePrograms.helocData !== undefined ? 'helocData' : 'helData';
    }
    return frontProgramType;
  }, [loanApplication?.selectedProgramType, loanApplication?.availablePrograms.helocData, loanApplication?.availablePrograms.helocRates, isICE]);
  const initialSelectedProgram = useMemo(() => {
    const hasIceStructure = loanApplication?.availablePrograms?.helRates || loanApplication?.availablePrograms?.helocRates;
    if (hasIceStructure) {
      return loanApplication?.selectedProgramType === 'Line of Credit' ? 'helocRates' : 'helRates';
    } else {
      return frontProgramType;
    }
  }, [loanApplication?.selectedProgramType, frontProgramType, loanApplication?.availablePrograms]);

  const isHelProgram = useMemo(() => {
    return loanApplication?.selectedProgramType === 'Line of Credit' ? 'isHiddenHeloc' : 'isHiddenHel';
  }, [loanApplication?.selectedProgramType]);
  const isHiddenLineAmount = !templateConfig?.pages?.vmo?.vmoInfo?.lineAmount?.[isHelProgram] || false;
  const isHiddenEstimatedApr = !templateConfig?.pages?.vmo?.estimatedAPR?.[isHelProgram];
  const isHiddenEstimatedMonthlyPmt = templateConfig?.pages?.vmo?.estimateMonthlyPmt?.[isHelProgram];

  const isMobile = useIsMobile(943);
  let auxLoanApplication = _.cloneDeep(loanApplication);

  const isTextColorBlackOrNotSpecified = (color: string | undefined): boolean => {
    if (!color) return true;
    const normalizedColor = color.toLowerCase().trim();
    return normalizedColor === '#000000' || normalizedColor === '#000' || normalizedColor === 'black' || normalizedColor === 'rgb(0,0,0)' || normalizedColor === 'rgba(0,0,0,1)';
  };

  const parseHtmlForTextColor = (htmlContent: string): string | undefined => {
    const hexColorMatch = htmlContent.match(/#[0-9A-Fa-f]{6}/);
    if (hexColorMatch) {
      return hexColorMatch[0];
    }

    const colorMatch = htmlContent.match(/color:\s*([^;"]+)/i);
    if (colorMatch) {
      return colorMatch[1].trim();
    }

    return undefined;
  };

  const shouldUseWhiteBackground = (program: Program): boolean => {
    if (program.customVerbiage) {
      const htmlColor = parseHtmlForTextColor(program.customVerbiage);

      if (!htmlColor || isTextColorBlackOrNotSpecified(htmlColor)) {
        return true;
      } else {
        return false;
      }
    }

    return isTextColorBlackOrNotSpecified(textColorVerbiage);
  };

  const isTextColorWhite = (color: string | undefined): boolean => {
    if (!color) return false;
    const normalizedColor = color.toLowerCase().trim();
    return (
      normalizedColor === '#ffffff' ||
      normalizedColor === '#fff' ||
      normalizedColor === 'white' ||
      normalizedColor === 'rgb(255,255,255)' ||
      normalizedColor === 'rgba(255,255,255,1)'
    );
  };

  const isLightColor = (color: string | undefined): boolean => {
    if (!color) return false;

    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 128;
    }

    const lightColors = ['white', '#ffffff', '#fff', 'yellow', 'lightyellow', 'lightgreen', 'lightblue', 'lightcyan', 'lightgray', 'lightgrey'];
    return lightColors.includes(color.toLowerCase());
  };

  const getVerbiageContainerBackground = (program?: Program): string => {
    if (isVerbiageCustomBackground) {
      return backgroundColorVerbiage;
    }

    if (program && shouldUseWhiteBackground(program)) {
      return '#ffffff';
    }

    const currentTextColor = program?.customVerbiage ? parseHtmlForTextColor(program.customVerbiage) : textColorVerbiage;

    if (isTextColorWhite(currentTextColor)) {
      const primaryColor = templateConfig?.globalTheme?.primaryColor;
      if (primaryColor && primaryColor !== '#5140E9') {
        return primaryColor;
      }
    }

    if (currentTextColor) {
      if (isLightColor(currentTextColor)) {
        const primaryColor = templateConfig?.globalTheme?.primaryColor;
        if (primaryColor && primaryColor !== '#5140E9') {
          return primaryColor;
        }
      } else {
        return '#ffffff';
      }
    } else {
      const primaryColor = templateConfig?.globalTheme?.primaryColor;
      if (primaryColor && primaryColor !== '#5140E9') {
        return primaryColor;
      }
    }

    return backgroundColorVerbiage;
  };

  useEffect(() => {
    setOpenPrograms(new Set());
  }, [initialSelectedProgram]);

  const handleProgramTypeSelection = useCallback(
    (program: Program) => {
      setSelectedProgram((prevProgram) => (prevProgram === program ? undefined : program));

      let newProgramType;
      if (program.programType) {
        newProgramType = program.programType === 'HEL' ? 'Home Equity Loan' : 'Line of Credit';
      } else {
        newProgramType = loanApplication?.selectedProgramType || 'Line of Credit';
      }

      auxLoanApplication.selectedProgramType = newProgramType;
      auxLoanApplication.auxActualMaxAmountApproved = Number(program.maxLineAmount);
      auxLoanApplication.actualMaxAmountApproved = 0;
      auxLoanApplication.maxAmountApproved = Number(program.maxLineAmount);

      const programWithDrawAmount = {
        ...program,
        drawAmount: program.drawAmount || 0,
      };

      const payload = handleProgramSelection(programWithDrawAmount, auxLoanApplication);

      dispatch({
        type: 'LoanApplication/updateValue',
        payload: { ...payload },
      });
    },
    [dispatch, auxLoanApplication, loanApplication?.selectedProgramType],
  );

  const handleModal = useCallback((label: string) => {
    const handle = HandleModal(label, templateConfig, LoanApplication);
    const infoModal = {
      body: handle.body,
      button: handle.button,
    };
    setInfoIcon(infoModal);
    setOpenModal(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleVerbiage = useCallback((programType: string) => {
    setOpenPrograms((prevOpenPrograms) => {
      const newOpenPrograms = new Set(prevOpenPrograms);
      if (newOpenPrograms.has(programType)) {
        newOpenPrograms.delete(programType);
      } else {
        newOpenPrograms.add(programType);
      }
      return newOpenPrograms;
    });
  }, []);

  const availablePrograms = useMemo(() => {
    const programData = loanApplication?.availablePrograms?.[initialSelectedProgram];
    // If the data has a 'rates' property, use it; otherwise use the data directly
    return programData?.rates || programData;
  }, [loanApplication?.availablePrograms, initialSelectedProgram]);

  useEffect(() => {
    setProgramName(loanApplication?.selectedProgram?.programName || '');
  }, [selectedProgram?.programName, loanApplication?.selectedProgram?.programName]);

  return (
    <WrapperContainer>
      <TitleContainer $isHidden={isHidden} $titleIsCustomColor={titleIsCustomColor} $titleCustomColor={titleCustomColor}>
        <TextBlock
          text={templateConfig?.pages?.vmo?.ProgramSelectorTitle?.text || 'Select a program that works for you.'}
          type={TypeOfText.Subtitle}
          isHidden={templateConfig?.pages?.vmo?.ProgramSelectorTitle?.isHidden || false}
        />
      </TitleContainer>
      {availablePrograms?.map((program: Program, index: React.Key | null | undefined) => (
        <ContainerPrograms key={index}>
          <SelectorContainer $backgroundColor={backgroundColor} $isBackgroundCustomColor={isBackgroundCustomColor}>
            <Program onClick={() => handleProgramTypeSelection(program)}>
              <ContainerIcon style={{ cursor: 'pointer' }} onClick={() => handleProgramTypeSelection(program)}>
                {programName === program?.programName ? <CheckedIcon /> : <ActiveIcon />}
              </ContainerIcon>
              <ProgramName color={textColorProgramName} $isTextCustomColor={isTextCustomColor} $textColorProgramName={textColorProgramName}>
                {!program?.alias ? <InitialProgramName $isHidden={templateConfig?.pages?.vmo?.typeOfProgram?.isHidden ?? false}> {program.programType}</InitialProgramName> : null}
                {program?.alias && program.alias !== '' ? program?.alias : program?.programName}
              </ProgramName>
            </Program>
            <Verbiage color={textColorVerbiage} $isVerbiageCustomColor={isVerbiageCustomColor} $textColorVerbiage={textColorVerbiage}>
              {isMobile ? 'Learn more' : templateConfig?.pages?.vmo?.verbiage?.text || 'Learn more about this loan program'}{' '}
              <DownIcon
                style={{ cursor: 'pointer' }}
                onClick={() => toggleVerbiage(program?.programName)}
                $isOpen={openPrograms.has(program?.programName)}
                data-testid="down-icon"
              />
            </Verbiage>
          </SelectorContainer>
          <DetailsContainer>
            {!openPrograms.has(program?.programName) ? (
              <>
                <DescriptionContainer>
                  <TextBlock
                    text={program?.description || program?.programName}
                    type={TypeOfText.Text}
                    direction="center"
                    isHidden={templateConfig?.pages?.vmo?.titleOfProgram?.isHidden ?? false}
                  />
                  <Row>
                    {(isICE || isHiddenLineAmount) && (
                      <>
                        <Col>
                          <ContainerInfoIcon>
                            <p>
                              {typeOfAmount('amountLabel', frontProgramType, isICE, program, templateConfig, loanApplication?.selectedProgramType) ||
                                (loanApplication?.selectedProgramType === 'Line of Credit' ? 'LINE AMOUNT' : 'LOAN AMOUNT')}
                            </p>
                            <InfoIcon
                              data-testid="info-icon"
                              onClick={() => {
                                const isHelocTab = loanApplication?.selectedProgramType === 'Line of Credit';
                                handleModal(isHelocTab ? 'LINE AMOUNT' : 'LOAN AMOUNT');
                              }}
                            />
                          </ContainerInfoIcon>
                          <h5>{formatNumber(Number(typeOfAmount('amountValue', frontProgramType, isICE, program, templateConfig, loanApplication?.selectedProgramType)))}</h5>
                        </Col>
                        <DivisorIcon />
                      </>
                    )}
                    {(() => {
                      const shouldShowEstMonthly = (isICE || !isHiddenEstimatedMonthlyPmt) && Boolean(program?.estimatedMonthlyPayment);
                      return shouldShowEstMonthly;
                    })() && (
                      <>
                        <Col>
                          <ContainerInfoIcon>
                            <p>{'EST MONTHLY PMT'}</p>
                            <InfoIcon data-testid="info-icon" onClick={() => handleModal('EST MONTHLY PMT')} />
                          </ContainerInfoIcon>
                          <h5>{formatNumber(Number(program?.estimatedMonthlyPayment))}</h5>
                        </Col>
                        <DivisorIcon />
                      </>
                    )}
                    {(isICE || isHiddenEstimatedApr) && program?.apr && (
                      <>
                        <Col>
                          <ContainerInfoIcon>
                            <p>{templateConfig?.pages?.vmo?.vmoInfo?.estimatedAPR?.estimatedAPRLabel || 'ESTIMATED APR'}</p>
                            <InfoIcon data-testid="info-icon" onClick={() => handleModal('ESTIMATED APR')} />
                          </ContainerInfoIcon>
                          <h5>{`${program?.apr}%`}</h5>
                        </Col>
                      </>
                    )}
                  </Row>
                </DescriptionContainer>
                <Footer>
                  <FooterDescription $isHidden={templateConfig?.pages?.vmo?.footerMaxAmount?.isHidden ?? false}>
                    {typeOfAmount('minAmountLabel', frontProgramType, isICE, program, templateConfig, loanApplication?.selectedProgramType) || `MIN LINE AMOUNT`}{' '}
                    {formatNumber(typeOfAmount('minValue', frontProgramType, isICE, program, templateConfig, loanApplication?.selectedProgramType))}
                  </FooterDescription>
                  <PointIcon />
                  <FooterDescription $isHidden={templateConfig?.pages?.vmo?.footerMaxAmount?.isHidden ?? false}>
                    {typeOfAmount('maxAmountLabel', frontProgramType, isICE, program, templateConfig, loanApplication?.selectedProgramType) || `MAX LINE AMOUNT`}{' '}
                    {formatNumber(typeOfAmount('maxValue', frontProgramType, isICE, program, templateConfig, loanApplication?.selectedProgramType))}
                  </FooterDescription>
                </Footer>
              </>
            ) : (
              (() => {
                const bgColor = getVerbiageContainerBackground(program);
                return (
                  <VerbiageContainer $isVerbiageCustomBackground={isVerbiageCustomBackground} $backgroundColorVerbiage={bgColor}>
                    <TextBlock
                      text={program?.customVerbiage || (program?.programType === 'HELOC' ? pages?.vmo?.vmoInfo?.helocVerbiage : pages?.vmo?.vmoInfo?.helVerbiage) || ''}
                      type={TypeOfText.Subtitle}
                      textAlign="justify"
                    />
                  </VerbiageContainer>
                );
              })()
            )}
          </DetailsContainer>
        </ContainerPrograms>
      ))}
      <FooterNote>
        <TextBlock
          text={templateConfig?.pages?.vmo?.footer?.text || 'Select a program that works for you.'}
          type={TypeOfText.Subtitle}
          isHidden={templateConfig?.pages?.vmo?.footer?.isHidden || false}
        />
      </FooterNote>
      {openModal && <DefaultModal openModal={openModal} setOpenModal={setOpenModal} infoModal={infoIcon} />}
    </WrapperContainer>
  );
};

export default AvailablePrograms;
